import * as api from "@/api/supply";

/** Источник WB «Распределения», движка и «Производства» — подписью. */
export const WB_SOURCE_LABEL: Record<api.WbSource, string> = {
  legacy: "старый (сырые таблицы)",
  mart: "витрина dwh",
};

/** Подпись источника; пустой — так и пишем, а не «старый» по умолчанию. */
export const wbSourceLabel = (s: api.WbSource | null | undefined): string =>
  s ? WB_SOURCE_LABEL[s] : "источник не указан";

/** Число с русской локалью, до 2 знаков. */
export const fmt = (n: number): string => n.toLocaleString("ru-RU", { maximumFractionDigits: 2 });

/** Целое, ru-локаль (для количеств/коробов). */
export const int0 = (n: number): string => Math.round(n).toLocaleString("ru-RU");

/** Число с 1 знаком после запятой, ru-локаль (скорость/коробы). */
export const dec1 = (n: number): string =>
  n.toLocaleString("ru-RU", { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/** Глубина запаса: ∞ при сентинеле (9999+), иначе целое. */
export const depthFmt = (n: number): string => (n >= 9999 ? "∞" : int0(n));

/** Двузначное число с ведущим нулём. Экспортируется: страницы собирают из него «ГГГГ-ММ» и
 *  «сегодня», чего готовые форматтеры не дают, — и копия такой однострочки уже заводилась. */
export const pad2 = (n: number): string => String(n).padStart(2, "0");

/** Дата в формате дд.мм.гггг (TZ-safe для строк YYYY-MM-DD). */
export function fmtDate(s: string | null | undefined): string {
  if (!s) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (m) return `${m[3]}.${m[2]}.${m[1]}`;
  const d = new Date(s);
  return Number.isNaN(d.getTime())
    ? String(s)
    : `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()}`;
}

/** Дата-время в формате дд.мм.гггг чч.мм (локальное). */
export function fmtDateTime(s: string | null | undefined): string {
  if (!s) return "—";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return String(s);
  return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()} ${pad2(
    d.getHours(),
  )}.${pad2(d.getMinutes())}`;
}

/** Число из `line.trace` (тип смешанный: число/строка/bool/null) — иначе null. */
export function traceNum(
  t: Record<string, number | string | boolean | null>,
  key: string,
): number | null {
  const v = t[key];
  return typeof v === "number" ? v : null;
}

/** Строка из `line.trace` — иначе null. */
export function traceStr(
  t: Record<string, number | string | boolean | null>,
  key: string,
): string | null {
  const v = t[key];
  return typeof v === "string" ? v : null;
}

/** Композитный ключ компонента (номенклатура|характеристика). */
export const compKey = (nom: string, char: string): string => `${nom}|${char}`;

/** Санитайз целочисленного ввода: только цифры, без ведущих нулей, можно очистить в пусто. */
export const sanitizeInt = (v: string): string => v.replace(/\D/g, "").replace(/^0+(?=\d)/, "");

/** Отображаемое имя компонента/позиции (fallback — короткий GUID номенклатуры). */
export function displayName(x: { name: string | null; nomenclature_key: string }): string {
  return x.name ?? x.nomenclature_key.slice(0, 8);
}

/** Ярлык канала: пустая строка = «МП (все)». */
export function channelLabel(ch: string): string {
  return ch === api.MP_CHANNEL ? "МП (все)" : ch;
}

/** Относительное время «N назад» (снимок — кэш, возраст всегда виден). */
export function timeAgo(iso: string): string {
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (min < 1) return "только что";
  if (min < 60) return `${min} мин назад`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} ч назад`;
  return `${Math.round(h / 24)} дн назад`;
}

/** Обратный отсчёт до истечения брони: «через N ч M мин» / «истёк». */
export function timeUntil(iso: string): string {
  const diffMin = Math.round((new Date(iso).getTime() - Date.now()) / 60000);
  if (diffMin <= 0) return "истёк";
  if (diffMin < 60) return `через ${diffMin} мин`;
  const h = Math.floor(diffMin / 60);
  const m = diffMin % 60;
  if (h < 24) return m ? `через ${h} ч ${m} мин` : `через ${h} ч`;
  return `через ${Math.floor(h / 24)} дн ${h % 24} ч`;
}

/** Алфавитная сортировка по названию (fallback — GUID номенклатуры). */
export function byName(
  a: { name: string | null; nomenclature_key: string },
  b: { name: string | null; nomenclature_key: string },
): number {
  return (a.name ?? a.nomenclature_key).localeCompare(b.name ?? b.nomenclature_key, "ru");
}

/** Русское склонение при числе: `plural(1, "место", "места", "мест")` → «1 место». */
export function plural(n: number, one: string, few: string, many: string): string {
  const mod100 = Math.abs(n) % 100;
  const mod10 = mod100 % 10;
  if (mod100 >= 11 && mod100 <= 14) return `${n} ${many}`;
  if (mod10 === 1) return `${n} ${one}`;
  if (mod10 >= 2 && mod10 <= 4) return `${n} ${few}`;
  return `${n} ${many}`;
}
